import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { lessonsAPI } from '../api/lessons.api';
import type { LessonDTO, AnswerDTO, LessonAnswerReportDTO } from '../core/types/lesson.types';
import type { Page, Pageable } from '../../../core/api/spring-types';
import { toast } from 'react-toastify';

// Query Keys
export const lessonKeys = {
  all: ['lessons'] as const,
  lists: () => [...lessonKeys.all, 'list'] as const,
  list: (type: string) => [...lessonKeys.lists(), type] as const,
  details: () => [...lessonKeys.all, 'detail'] as const,
  detail: (id: string) => [...lessonKeys.details(), id] as const,
  answers: () => ['lesson-answers'] as const,
  answersForLesson: (lessonId: string) => [...lessonKeys.answers(), lessonId] as const,
  answersForUser: (lessonId: string, userId: string) =>
    [...lessonKeys.answers(), lessonId, userId] as const,
  reports: () => ['lesson-answer-reports'] as const,
  report: (filter: Partial<LessonAnswerReportDTO>, pageable: Pageable) =>
    [...lessonKeys.reports(), filter, pageable] as const,
};

// Backwards compatibility alias
export const lessonQueryKeys = lessonKeys;

// ==================== LESSON QUERIES ====================

/**
 * Hook to fetch a single lesson by ID
 */
export function useLessonQuery(
  lessonId: string,
  options?: Omit<UseQueryOptions<LessonDTO, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: lessonKeys.detail(lessonId),
    queryFn: () => lessonsAPI.getById(lessonId),
    enabled: !!lessonId,
    ...options,
  });
}

/**
 * Hook to fetch lessons by type
 */
export function useLessonsQuery(
  type: string,
  filters?: Record<string, any>,
  options?: Omit<UseQueryOptions<LessonDTO[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: lessonKeys.list(type),
    queryFn: () => lessonsAPI.listByType(type, filters),
    enabled: !!type,
    ...options,
  });
}

// ==================== LESSON MUTATIONS ====================

/**
 * Hook to create a new lesson
 */
export function useCreateLessonMutation(
  options?: UseMutationOptions<LessonDTO, Error, LessonDTO>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lesson: LessonDTO) => lessonsAPI.create(lesson),
    onSuccess: (data, _variables) => {
      // Invalidate lesson lists
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // Add to cache
      queryClient.setQueryData(lessonKeys.detail(data.id!), data);
      toast.success('Lecția a fost creată cu succes');
    },
    onError: (error) => {
      toast.error('Eroare la crearea lecției');
      console.error('Create lesson error:', error);
    },
    ...options,
  });
}

/**
 * Hook to update an existing lesson
 */
export function useUpdateLessonMutation(
  options?: UseMutationOptions<LessonDTO, Error, LessonDTO>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lesson: LessonDTO) => lessonsAPI.update(lesson),
    onSuccess: (data, _variables) => {
      // Invalidate lesson lists
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // Update cache
      queryClient.setQueryData(lessonKeys.detail(data.id!), data);
      toast.success('Lecția a fost actualizată cu succes');
    },
    onError: (error) => {
      toast.error('Eroare la actualizarea lecției');
      console.error('Update lesson error:', error);
    },
    ...options,
  });
}

/**
 * Hook to save a lesson (create or update)
 */
export function useSaveLessonMutation(
  options?: UseMutationOptions<LessonDTO, Error, LessonDTO>
) {
  const createMutation = useCreateLessonMutation();
  const updateMutation = useUpdateLessonMutation();

  return useMutation({
    mutationFn: (lesson: LessonDTO) => {
      if (lesson.id) {
        return updateMutation.mutateAsync(lesson);
      } else {
        return createMutation.mutateAsync(lesson);
      }
    },
    ...options,
  });
}

/**
 * Hook to delete a lesson
 */
export function useDeleteLessonMutation(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => lessonsAPI.delete(id),
    onSuccess: (_, id) => {
      // Invalidate lesson lists
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      // Remove from cache
      queryClient.removeQueries({ queryKey: lessonKeys.detail(id) });
      toast.success('Lecția a fost ștearsă cu succes');
    },
    onError: (error) => {
      toast.error('Eroare la ștergerea lecției');
      console.error('Delete lesson error:', error);
    },
    ...options,
  });
}

/**
 * Hook to toggle lesson visibility
 */
export function useToggleLessonVisibilityMutation(
  options?: UseMutationOptions<void, Error, { id: string; isHidden: boolean }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isHidden }) => lessonsAPI.toggleVisibility(id, isHidden),
    onSuccess: (_, variables) => {
      // Invalidate lesson lists and detail
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(variables.id) });
      toast.success(`Lecția a fost ${variables.isHidden ? 'ascunsă' : 'afișată'}`);
    },
    onError: (error) => {
      toast.error('Eroare la schimbarea vizibilității');
      console.error('Toggle visibility error:', error);
    },
    ...options,
  });
}

// ==================== ANSWER QUERIES ====================

/**
 * Hook to fetch answers for a lesson
 */
export function useLessonAnswersQuery(
  lessonId: string,
  options?: Omit<UseQueryOptions<AnswerDTO[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: lessonKeys.answersForLesson(lessonId),
    queryFn: () => lessonsAPI.getAnswersForLesson(lessonId),
    enabled: !!lessonId,
    ...options,
  });
}

/**
 * Hook to fetch answers for the current user's lesson attempt (student workflow)
 * User is automatically determined from JWT token
 *
 * Note: version parameter removed in state machine architecture.
 * Backend now returns answers for the user's current/latest attempt.
 *
 * For teachers viewing specific student answers, use a different hook.
 */
export function useUserLessonAnswersQuery(
  lessonId: string,
  userId: string, // Keep for backwards compatibility, not used in API call
  options?: Omit<UseQueryOptions<AnswerDTO[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: lessonKeys.answersForUser(lessonId, userId),
    queryFn: () => lessonsAPI.getAnswersForCurrentUser(lessonId),
    enabled: !!lessonId,
    ...options,
  });
}

// ==================== ANSWER MUTATIONS ====================

/**
 * Hook to submit an answer for a slide
 */
export function useSubmitAnswerMutation(
  options?: UseMutationOptions<AnswerDTO, Error, AnswerDTO>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (answer: AnswerDTO) => lessonsAPI.submitAnswer(answer),
    onSuccess: (newAnswer) => {
      // Update all answer query caches directly with the new answer (no refetch needed)
      // This updates any existing caches (answersForLesson, answersForUser, etc.)
      queryClient.setQueriesData(
        { queryKey: lessonKeys.answers() }, // Matches all answer queries
        (oldData: AnswerDTO[] | undefined) => {
          if (!oldData) return oldData; // Don't create new cache entries
          return [...oldData, newAnswer]; // Add new answer to existing list
        }
      );
      // Don't show toast for each slide answer, only for lesson completion
    },
    onError: (error) => {
      toast.error('Eroare la trimiterea răspunsului');
      console.error('Submit answer error:', error);
    },
    ...options,
  });
}

// ==================== REPORT QUERIES ====================

/**
 * Hook to fetch lesson answers report with pagination
 */
export function useLessonAnswersReportQuery(
  filter: Partial<LessonAnswerReportDTO>,
  pageable: Pageable,
  options?: Omit<UseQueryOptions<Page<LessonAnswerReportDTO>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: lessonKeys.report(filter, pageable),
    queryFn: () => lessonsAPI.getAnswersReport(filter, pageable),
    ...options,
  });
}

// ==================== APPROVAL MUTATIONS ====================

/**
 * Hook to update answer approval status
 */
export function useUpdateAnswerApprovalMutation(
  options?: UseMutationOptions<void, Error, {
    id: string;
    isApproved?: boolean;
    rejectReason?: string;
  }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isApproved, rejectReason }) =>
      lessonsAPI.updateApproval(id, isApproved, rejectReason),
    onSuccess: (_, variables) => {
      // Invalidate reports
      queryClient.invalidateQueries({ queryKey: lessonKeys.reports() });

      if (variables.isApproved === true) {
        toast.success('Răspunsul a fost aprobat');
      } else if (variables.isApproved === false) {
        toast.success('Răspunsul a fost respins');
      }
    },
    onError: (error) => {
      toast.error('Eroare la actualizarea statusului');
      console.error('Update approval error:', error);
    },
    ...options,
  });
}

// ==================== DEFAULT TEMPLATES ====================

/**
 * Hook to fetch default lesson template
 */
export function useDefaultLessonQuery(
  options?: Omit<UseQueryOptions<LessonDTO, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['default-lesson'],
    queryFn: () => lessonsAPI.getDefaultLesson(),
    ...options,
  });
}

/**
 * Hook to fetch default slide template
 */
export function useDefaultSlideQuery(
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['default-slide'],
    queryFn: () => lessonsAPI.getDefaultSlide(),
    ...options,
  });
}

/**
 * Hook to fetch default answer template
 */
export function useDefaultAnswerQuery(
  options?: Omit<UseQueryOptions<AnswerDTO, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['default-answer'],
    queryFn: () => lessonsAPI.getDefaultAnswer(),
    ...options,
  });
}

// ==================== COMBINED HOOKS ====================

/**
 * Hook to fetch lesson and user's existing answers together
 * Optimized for lesson completion workflow
 *
 * @example
 * const { lesson, answers, isLoading } = useLessonWithAnswers('lesson-123', 'user-456');
 */
export function useLessonWithAnswers(
  lessonId: string | undefined,
  userId: string | undefined,
  options?: {
    enabled?: boolean;
    skipAnswers?: boolean;
  }
) {
  const lessonQuery = useLessonQuery(lessonId!, { enabled: (options?.enabled ?? true) && !!lessonId });

  // ✅ FIX: Load answers in parallel with lesson (removed !!lessonQuery.data dependency)
  // This eliminates race condition where lesson loads first, then answers load second
  // Both queries can now run simultaneously for faster resume detection
  const answersQuery = useUserLessonAnswersQuery(lessonId!, userId!, {
    enabled: (options?.enabled ?? true) && !options?.skipAnswers && !!lessonId && !!userId,
  });

  // Memoize answers to prevent creating new array references on every render
  // This prevents infinite loop in useEffect when answers is undefined
  const stableAnswers = useMemo(
    () => answersQuery.data || [],
    [answersQuery.data]
  );

  // 🔍 DEBUG: Log query state (moved to useEffect to prevent render loop)
  // useEffect(() => {
  //   console.log('📡 useLessonWithAnswers Query:', {
  //     hasLesson: !!lessonQuery.data,
  //     hasAnswers: !!answersQuery.data,
  //     answersCount: answersQuery.data?.length || 0,
  //     lessonLoading: lessonQuery.isLoading,
  //     answersLoading: answersQuery.isLoading,
  //     lessonError: lessonQuery.isError,
  //     answersError: answersQuery.isError,
  //     lessonEnabled: (options?.enabled ?? true) && !!lessonId,
  //     answersEnabled: (options?.enabled ?? true) && !options?.skipAnswers && !!lessonId && !!userId && !!lessonQuery.data,
  //     skipAnswers: options?.skipAnswers,
  //   });
  // }, [lessonQuery.data, answersQuery.data, lessonQuery.isLoading, answersQuery.isLoading, lessonQuery.isError, answersQuery.isError, lessonId, userId, options]);

  return {
    lesson: lessonQuery.data,
    answers: stableAnswers,
    isLoading: lessonQuery.isLoading || answersQuery.isLoading,
    isError: lessonQuery.isError || answersQuery.isError,
    error: lessonQuery.error || answersQuery.error,
    refetchLesson: lessonQuery.refetch,
    refetchAnswers: answersQuery.refetch,
  };
}